import { Accordion, Card, Col, Container, Row, Table } from "react-bootstrap";
import { AppAuthStateProps } from '../../types/propTypes';

import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { COMPASS_ACCRUAL_PARTICIPANTS, COMPASS_ACCRUAL_VISITS_AFTER_INDEX, COMPASS_PAST_MEDICAL_HISTORY, callQuery } from "../../amplify/api-gateway/query";
import { CompassParticipant, PatientVisit, PatientVisitMetadata } from '../../amplify/api-gateway/search-patients';
import { isDemoUser } from "../../utils/demo";
import { PLOT_BLUE, PLOT_GREEN, PLOT_PURPLE } from "../../utils/plot";
import { generateDates, getDayTruncatedDate, getDaysBetweenDates, getPacificDateString, withTimezone } from "../../utils/time";

export const CompassAccrual = (props: AppAuthStateProps) => {

    const [isLoading, setIsLoading] = useState(false);
    const [numCompleted, setNumCompleted] = useState(0);
    const [completedWithRevisitPatients, setCompletedWithRevisitPatients] = useState<any[]>([]);
    const [completedWithRevisitDischargedPatients, setCompletedWithRevisitDischargedPatients] = useState<any[]>([]);
    const [completedWithoutRevisitPatients, setCompletedWithoutRevisitPatients] = useState<any[]>([]);
    const [completedWithRevisitAdmitPatients, setCompletedWithRevisitAdmitPatients] = useState<any[]>([]);
    const [revisitDischargeDiagnoses, setRevisitDischargeDiagnoses] = useState([]);
    const [revisitAdmitDiagnoses, setRevisitAdmitDiagnoses] = useState([]);
    const [pastMedicalHistory, setPastMedicalHistory] = useState<any>({});
    const [numEnrolled, setNumEnrolled] = useState(0);
    const [numActive, setNumActive] = useState(0);
    const [activePatients, setActivePatients] = useState<any[]>([]);
    const [numWithdrawn, setNumWithdrawn] = useState(0);
    const [withdrawnPatients, setWithdrawnPatients] = useState<any[]>([]);
    const [accrualTrends, setAccrualTrends] = useState<any>();

    useEffect(() => {
        setIsLoading(true);
        const promises = [
            callQuery({
                operation: COMPASS_ACCRUAL_PARTICIPANTS,
            }),
            callQuery({
                operation: COMPASS_ACCRUAL_VISITS_AFTER_INDEX,
            }),
            callQuery({
                operation: COMPASS_PAST_MEDICAL_HISTORY,
            }),
        ];
        Promise.all(promises).then((results) => {
            const participants = results[0] as (CompassParticipant & PatientVisit & PatientVisitMetadata)[];
            const visitsAfterIndex = results[1] as any[];
            const pastMedicalHistoryArr = results[2] as any[];
            const mrnToEarliestRevisitArrival: any = {};
            const categoryToCount: any = {...pastMedicalHistory};

            for (let visit of visitsAfterIndex) {
                if (!visit.is_index_visit && !mrnToEarliestRevisitArrival[visit.mrn]) {
                    mrnToEarliestRevisitArrival[visit.mrn] = visit;
                }
            }

            for (let history of pastMedicalHistoryArr) {
                const text = history.category;
                if (text) {
                    if (!categoryToCount[text]) {
                        categoryToCount[text] = 0;
                    }
                    categoryToCount[text] += Number(history.count);
                }
            }
            setPastMedicalHistory(categoryToCount);

            setIsLoading(false);

            let numEnrolled = participants.length;
            let numWithdrawn = 0;
            let withdrawnPatients: any = [];
            let numCompleted = 0;
            let completedWithRevisitPatients: any = [];
            let completedWithRevisitDischargedPatients: any = [];
            let completedWithoutRevisitPatients: any = [];
            let completedWithRevisitAdmitPatients: any = [];
            let numActive = 0;
            let activePatients: any = [];
            const dateRange = generateDates(getDayTruncatedDate(withTimezone(new Date(2024, 10, 1), "America/Los_Angeles")), getDayTruncatedDate(new Date()));
            const dateToStats: any = {};

            const revisitDischargeDiagnosisToCount: any = {};
            const revisitAdmitDiagnosisToCount: any = {};

            for (let p of participants) {
                if (p.withdrawn === "1") {
                    numWithdrawn += 1;
                    withdrawnPatients.push(p);
                } else if (getDaysBetweenDates(new Date(p.enrollment_date), new Date()) >= 30) {
                    numCompleted += 1;

                    const revisit: PatientVisit = mrnToEarliestRevisitArrival[p.mrn];
                    if (revisit && getDaysBetweenDates(new Date(revisit.arrival_time), new Date(p.enrollment_date)) <= 30) {
                        // There is revisit within 30 days
                        completedWithRevisitPatients.push([p, revisit]);

                        if (revisit.ed_dispo === "Discharge" || revisit.ed_dispo === "") {
                            completedWithRevisitDischargedPatients.push(p);
                        } else {
                            completedWithRevisitAdmitPatients.push(p);
                        }

                        if (revisit.diagnoses) {
                            if (revisit.ed_dispo === "Discharge" || revisit.ed_dispo === "") {
                                for (let diagnosis of revisit.diagnoses) {
                                    if (!revisitDischargeDiagnosisToCount[diagnosis]) {
                                        revisitDischargeDiagnosisToCount[diagnosis] = 0;
                                    }
                                    revisitDischargeDiagnosisToCount[diagnosis]++;
                                }
                            } else {
                                for (let diagnosis of revisit.diagnoses) {
                                    if (!revisitAdmitDiagnosisToCount[diagnosis]) {
                                        revisitAdmitDiagnosisToCount[diagnosis] = 0;
                                    }
                                    revisitAdmitDiagnosisToCount[diagnosis]++;
                                }
                            }
                        }
                    } else {
                        completedWithoutRevisitPatients.push(p);
                    }
                } else {
                    numActive += 1;
                    activePatients.push(p);
                }
            }

            let reivsitDischargeDiagnosisCounts: any = [];
            for (let diagnosis of Object.keys(revisitDischargeDiagnosisToCount)) {
                reivsitDischargeDiagnosisCounts.push({
                    diagnosis: diagnosis,
                    count: revisitDischargeDiagnosisToCount[diagnosis],
                });
            }
            reivsitDischargeDiagnosisCounts.sort((a: any, b: any) => b.count - a.count);

            let reivsitAdmitDiagnosisCounts: any = [];
            for (let diagnosis of Object.keys(revisitAdmitDiagnosisToCount)) {
                reivsitAdmitDiagnosisCounts.push({
                    diagnosis: diagnosis,
                    count: revisitAdmitDiagnosisToCount[diagnosis],
                });
            }
            reivsitAdmitDiagnosisCounts.sort((a: any, b: any) => b.count - a.count);

            setNumCompleted(numCompleted);
            setNumActive(numActive);
            setNumEnrolled(numEnrolled);
            setNumWithdrawn(numWithdrawn);
            setWithdrawnPatients(withdrawnPatients);
            setCompletedWithRevisitPatients(completedWithRevisitPatients);
            setCompletedWithRevisitDischargedPatients(completedWithRevisitDischargedPatients);
            setCompletedWithoutRevisitPatients(completedWithoutRevisitPatients);
            setCompletedWithRevisitAdmitPatients(completedWithRevisitAdmitPatients);
            setActivePatients(activePatients);
            setRevisitDischargeDiagnoses(reivsitDischargeDiagnosisCounts);
            setRevisitAdmitDiagnoses(reivsitAdmitDiagnosisCounts);

            for (let dt of dateRange) {
                const dtStr = getPacificDateString(dt);
                dateToStats[dtStr] = {
                    "enrolled": 0,
                    "completed-30": 0,
                    "completed-revisit": 0,
                };

                for (let p of participants) {
                    if (new Date(p.enrollment_date) <= dt) {
                        dateToStats[dtStr]["enrolled"] += 1;
                        const revisit = mrnToEarliestRevisitArrival[p.mrn];
                        if (revisit && getDaysBetweenDates(new Date(revisit.arrival_time), new Date(p.enrollment_date)) <= 30) {
                            // Revisit occurred on this date
                            dateToStats[dtStr]["completed-revisit"] += 1;
                        } else {
                            const daysSinceEnrollment = getDaysBetweenDates(dt, new Date(p.enrollment_date));
                            if (daysSinceEnrollment >= 30) {
                                dateToStats[dtStr]["completed-30"] += 1;
                            }
                        }
                    }
                }
            }

            setAccrualTrends({
                datasets: [
                    {
                        label: 'Enrolled',
                        data: dateRange.map((dt, idx) => {
                            let dtStr = getPacificDateString(dt);
                            return {
                                x: dtStr,
                                y: dateToStats[dtStr]["enrolled"],
                            };
                        }),
                        borderColor: PLOT_BLUE,
                        lineTension: 0,
                        spanGaps: false,
                        fill: false,
                    },{
                        label: 'Completed (30-day)',
                        data: dateRange.map((dt, idx) => {
                            let dtStr = getPacificDateString(dt);
                            return {
                                x: dtStr,
                                y: dateToStats[dtStr]["completed-30"],
                            };
                        }),
                        borderColor: PLOT_GREEN,
                        lineTension: 0,
                        spanGaps: false,
                        fill: false,
                    },{
                        label: 'Completed (ED revisit)',
                        data: dateRange.map((dt, idx) => {
                            let dtStr = getPacificDateString(dt);
                            return {
                                x: dtStr,
                                y: dateToStats[dtStr]["completed-revisit"],
                            };
                        }),
                        borderColor: PLOT_PURPLE,
                        lineTension: 0,
                        spanGaps: false,
                        fill: false,
                    },
                ]
            });
        });
    }, []);

    const computeStats = (patients: any[], isWithdrawn: boolean, anchorDates?: Date[]) => {
        const ages = patients.map(p => Number(p.age));
        const female = patients.filter(p => p.gender === "F");
        let monitorPercentage = 0;
        let surveyPercentage = 0;
        if (!isWithdrawn) {
            const monitorPercentages = patients.map((p, idx) => {
                if (p.monitor_counts) {
                    let daysBetween;
                    if (anchorDates) {
                        daysBetween = getDaysBetweenDates(anchorDates[idx], new Date(p.enrollment_date));
                    } else {
                        daysBetween = 30;
                    }
                    return Math.round(1000 * Number(p.monitor_counts) / daysBetween) / 10;
                } else {
                    return 0;
                }
            });
            monitorPercentage = Math.round(10 * monitorPercentages.reduce((sum, item) => sum + item, 0) / monitorPercentages.length) / 10;
            const surveyPercentages = patients.map((p, idx) => {
                if (p.survey_counts) {
                    let daysBetween;
                    if (anchorDates) {
                        daysBetween = getDaysBetweenDates(anchorDates[idx], new Date(p.enrollment_date));
                    } else {
                        daysBetween = 30;
                    }
                    return Math.round(1000 * Number(p.survey_counts) / daysBetween) / 10;
                } else {
                    return 0;
                }
            });
            surveyPercentage = Math.round(10 * surveyPercentages.reduce((sum, item) => sum + item, 0) / surveyPercentages.length) / 10;
        }

        return {
            "median_age": median(ages),
            "iqr_age": {
                "Q1": quantile(ages, 0.25),
                "Q3": quantile(ages, 0.75),
            },
            "female": Math.round(1000 * female.length / patients.length) / 10,
            "mean_monitoring": monitorPercentage,
            "mean_survey": surveyPercentage,
        };
    }

    const activeParticipantsStats = useMemo(() => {
        if (activePatients.length > 0) {
            return computeStats(activePatients, false, Array(activePatients.length).fill(new Date()));
        } else {
            return null;
        }
    }, [activePatients]);

    const completedWithRevisitPatientsStats = useMemo(() => {
        if (completedWithRevisitPatients.length > 0) {
            return computeStats(completedWithRevisitPatients.map(arr => arr[0]), false, completedWithRevisitPatients.map(arr => arr[1].arrival_time));
        } else {
            return null;
        }
    }, [completedWithRevisitPatients]);

    const completedWithoutRevisitPatientsStats = useMemo(() => {
        if (completedWithoutRevisitPatients.length > 0) {
            return computeStats(completedWithoutRevisitPatients, false);
        } else {
            return null;
        }
    }, [completedWithoutRevisitPatients]);

    const withdrawnPatientsStats = useMemo(() => {
        if (withdrawnPatients.length > 0) {
            return computeStats(withdrawnPatients, true);
        } else {
            return null;
        }
    }, [withdrawnPatients]);

    function median(arr: number[]): number {
        if (arr.length === 0) throw new Error("Array is empty");
        
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
    
        return sorted.length % 2 !== 0
            ? sorted[mid] // Odd length
            : (sorted[mid - 1] + sorted[mid]) / 2; // Even length
    }

    function quantile(arr: number[], q: number): number {
        const sortedArr = [...arr].sort((a, b) => a - b);
        const pos = (sortedArr.length - 1) * q;
        const base = Math.floor(pos);
        const rest = pos - base;
    
        if (sortedArr[base + 1] !== undefined) {
            return sortedArr[base] + rest * (sortedArr[base + 1] - sortedArr[base]);
        } else {
            return sortedArr[base];
        }
    }

    const baseChartOptions = {
        maintainAspectRatio: false,
        scales: {
            x: {
                type: 'time',
                time: {
                  unit: 'day',
                  displayFormats: {
                      day: 'MMM DD'
                  },
                },
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    color: 'rgba(107, 114, 128, 0.1)'
                },
            }
        },
        plugins: {
            legend: {
                display: true,
                align: 'start',
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyleWidth: 16,
                    pointStyle: 'rectRounded',
                },
            },
            tooltip: {
                enabled: true,
                mode: 'index' as const,
                intersect: false
            }
        },
        interaction: {
            mode: 'index' as const,
            intersect: false
        }
    }

    const getNumericsChartOptions = (): any => {
        return {
            ...baseChartOptions,
            scales: {
                ...baseChartOptions.scales,
                y: {
                    beginAtZero: true,
                    grid: {
                        color: 'rgba(107, 114, 128, 0.1)'
                    },
                    ticks: {
                        color: '#6b7280',
                    },
                }
            },
            plugins: {
                ...baseChartOptions.plugins,
                tooltip: {
                    ...baseChartOptions.plugins.tooltip,
                    mode: 'nearest',
                    callbacks: {
                        label: (context: any) => context.raw.y,
                        title: (tooltipItems: any[]) => {
                            if (tooltipItems[0]) {
                                return `${getPacificDateString(new Date(tooltipItems[0].parsed.x))}`
                            }
                            return '';
                        }
                    }
                }
            },
            interaction: {
                ...baseChartOptions.interaction,
                mode: 'nearest'
            },
        };
    }

    return (
        <>
            <Container style={{ paddingTop: isDemoUser(props.user) ? '143px' : '64px', paddingLeft: '32px', paddingRight: '32px' }} fluid>

                <Row className='mb-2'>
                    <h4>COMPASS Study Summary</h4>
                </Row>
                {
                    isLoading ? (
                        <Container fluid style={{paddingTop: '100px', textAlign: 'center'}}>
                            <Row>
                                <Col md={12}>
                                    <h1 style={{color: '#AAA', fontSize: '16px'}}><FontAwesomeIcon spin icon={faSpinner} /> Loading...</h1>
                                </Col>
                            </Row>
                        </Container>
                    ) : (
                        <>
                            <Row className='mb-2'>
                                <Col md={3}>
                                    <Card>
                                        <Card.Body>
                                            <Container>
                                                <Col>
                                                    <Row className="mb-3">
                                                        Enrolled
                                                    </Row>
                                                    <Row>
                                                        <h2>{numEnrolled}</h2>
                                                    </Row>
                                                </Col>
                                            </Container>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card>
                                        <Card.Body>
                                            <Container>
                                                <Col>
                                                    <Row className="mb-3">
                                                        Withdrawn
                                                    </Row>
                                                    <Row>
                                                        <h2>{numWithdrawn}</h2>
                                                    </Row>
                                                </Col>
                                            </Container>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card>
                                        <Card.Body>
                                            <Container>
                                                <Col>
                                                    <Row className="mb-3">
                                                        Completed
                                                    </Row>
                                                    <Row>
                                                        <h2>{numCompleted}</h2>
                                                    </Row>
                                                </Col>
                                            </Container>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card>
                                        <Card.Body>
                                            <Container>
                                                <Col>
                                                    <Row className="mb-3">
                                                        Active
                                                    </Row>
                                                    <Row>
                                                        <h2>{numActive}</h2>
                                                    </Row>
                                                </Col>
                                            </Container>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                            <Row style={{maxHeight: '400px'}} className="mt-4 mb-4">
                                <h4>Cumulative Enrollment</h4>
                                {
                                    accrualTrends && (
                                        <Line
                                            data={accrualTrends}
                                            options={getNumericsChartOptions()}
                                        />
                                    )
                                }
                            </Row>
                            <Row style={{marginTop: '64px'}}>
                                <h4>ED Revisit Statistics</h4>
                                <p>
                                    Of {numCompleted} completed participants, {completedWithRevisitPatients.length} ({Math.round(1000 * completedWithRevisitPatients.length / numCompleted) / 10}%) returned to the ED within 30 days of enrollment.
                                </p>
                                <p>
                                    Of {completedWithRevisitPatients.length} ED revisits, {completedWithRevisitDischargedPatients.length} ({Math.round(1000 * completedWithRevisitDischargedPatients.length / completedWithRevisitPatients.length) / 10}%) were discharged with diagnoses:
                                </p>
                                <Accordion alwaysOpen>
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>Discharge Diagnoses</Accordion.Header>
                                        <Accordion.Body>
                                            <Col className='table-fixed-header'>
                                                <Table size="sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Diagnosis</th>
                                                            <th>Count</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {
                                                            revisitDischargeDiagnoses.map((p: any) => {
                                                                return (
                                                                    <tr>
                                                                        <td>{p.diagnosis}</td>
                                                                        <td>{p.count} ({Math.round(1000 * p.count / completedWithRevisitDischargedPatients.length) / 10}%)</td>
                                                                    </tr>
                                                                )
                                                            })
                                                        }
                                                    </tbody>
                                                </Table>
                                            </Col>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    <Accordion.Item eventKey="1">
                                        <Accordion.Header>Admission Diagnoses</Accordion.Header>
                                        <Accordion.Body>
                                            <Col className='table-fixed-header'>
                                                <Table size="sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Diagnosis</th>
                                                            <th>Count</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {
                                                            revisitAdmitDiagnoses.map((p: any) => {
                                                                return (
                                                                    <tr>
                                                                        <td>{p.diagnosis}</td>
                                                                        <td>{p.count} ({Math.round(1000 * p.count / completedWithRevisitAdmitPatients.length) / 10}%)</td>
                                                                    </tr>
                                                                )
                                                            })
                                                        }
                                                    </tbody>
                                                </Table>
                                            </Col>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </Row>
                            <Row style={{marginTop: '64px'}}>
                                <h4>Active Participants (n={numActive})</h4>
                                {
                                    activeParticipantsStats && (
                                        <ul>
                                            <li>Median Age: {activeParticipantsStats.median_age} (IQR: {activeParticipantsStats.iqr_age.Q1}-{activeParticipantsStats.iqr_age.Q3})</li>
                                            <li>Female: {activeParticipantsStats.female}%</li>
                                            <li>Mean Monitoring: {activeParticipantsStats.mean_monitoring}%</li>
                                            <li>Mean Survey: {activeParticipantsStats.mean_survey}%</li>
                                        </ul>
                                    )
                                }
                                <Col className='table-fixed-header'>
                                    <Table size="sm">
                                        <thead>
                                            <tr>
                                                <th>Study ID</th>
                                                <th>Enrollment Date</th>
                                                <th>Age</th>
                                                <th>Gender</th>
                                                <th>Days Enrolled</th>
                                                <th>Days Remaining</th>
                                                <th>Monitor %</th>
                                                <th>Survey %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                activePatients.map((p: (PatientVisit & CompassParticipant)) => {
                                                    const daysBetween = getDaysBetweenDates(new Date(), new Date(p.enrollment_date));
                                                    return (
                                                        <tr>
                                                            <td><a href={`/compass/${p.study_id}`} target="_blank">{p.study_id}</a></td>
                                                            <td>{p.enrollment_date}</td>
                                                            <td>{p.age}</td>
                                                            <td>{p.gender}</td>
                                                            <td>{daysBetween}</td>
                                                            <td>{30 - daysBetween}</td>
                                                            <td>{Math.round(1000 * Number(p.monitor_counts) / daysBetween) / 10} ({p.monitor_counts}/{daysBetween})</td>
                                                            <td>{Math.round(1000 * Number(p.survey_counts) / daysBetween) / 10} ({p.survey_counts}/{daysBetween})</td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                            <Row style={{marginTop: '64px'}}>
                                <h4>Completed With Revisit Participants (n={completedWithRevisitPatients.length})</h4>
                                {
                                    completedWithRevisitPatientsStats && (
                                        <ul>
                                            <li>Median Age: {completedWithRevisitPatientsStats.median_age} (IQR: {completedWithRevisitPatientsStats.iqr_age.Q1}-{completedWithRevisitPatientsStats.iqr_age.Q3})</li>
                                            <li>Female: {completedWithRevisitPatientsStats.female}%</li>
                                            <li>Mean Monitoring: {completedWithRevisitPatientsStats.mean_monitoring}%</li>
                                            <li>Mean Survey: {completedWithRevisitPatientsStats.mean_survey}%</li>
                                        </ul>
                                    )
                                }
                                <Col className='table-fixed-header'>
                                    <Table size="sm">
                                        <thead>
                                            <tr>
                                                <th>Study ID</th>
                                                <th>Enrollment Date</th>
                                                <th>Revisit Date</th>
                                                <th>Age</th>
                                                <th>Gender</th>
                                                <th>Days Enrolled</th>
                                                <th>Monitor %</th>
                                                <th>Survey %</th>
                                                <th>Revisit Dispo</th>
                                                <th>Revisit Diagnoses</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                completedWithRevisitPatients.map((arr: any) => {
                                                    const p: (PatientVisit & CompassParticipant) = arr[0];
                                                    const revisit: (PatientVisit) = arr[1];
                                                    const daysBetween = getDaysBetweenDates(new Date(revisit.arrival_time), new Date(p.enrollment_date));
                                                    return (
                                                        <tr>
                                                            <td><a href={`/compass/${p.study_id}`} target="_blank">{p.study_id}</a></td>
                                                            <td>{p.enrollment_date}</td>
                                                            <td>{revisit.arrival_time}</td>
                                                            <td>{p.age}</td>
                                                            <td>{p.gender}</td>
                                                            <td>{daysBetween}</td>
                                                            <td>{Math.round(1000 * Number(p.monitor_counts) / daysBetween) / 10} ({p.monitor_counts}/{daysBetween})</td>
                                                            <td>{Math.round(1000 * Number(p.survey_counts) / daysBetween) / 10} ({p.survey_counts}/{daysBetween})</td>
                                                            <td>{revisit.ed_dispo}</td>
                                                            <td>{revisit.diagnoses}</td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                            <Row style={{marginTop: '64px'}}>
                                <h4>Completed Without Revisit Participants (n={completedWithoutRevisitPatients.length})</h4>
                                {
                                    completedWithoutRevisitPatientsStats && (
                                        <ul>
                                            <li>Median Age: {completedWithoutRevisitPatientsStats.median_age} (IQR: {completedWithoutRevisitPatientsStats.iqr_age.Q1}-{completedWithoutRevisitPatientsStats.iqr_age.Q3})</li>
                                            <li>Female: {completedWithoutRevisitPatientsStats.female}%</li>
                                            <li>Mean Monitoring: {completedWithoutRevisitPatientsStats.mean_monitoring}%</li>
                                            <li>Mean Survey: {completedWithoutRevisitPatientsStats.mean_survey}%</li>
                                        </ul>
                                    )
                                }
                                <Col className='table-fixed-header'>
                                    <Table size="sm">
                                        <thead>
                                            <tr>
                                                <th>Study ID</th>
                                                <th>Enrollment Date</th>
                                                <th>Age</th>
                                                <th>Gender</th>
                                                <th>Days Enrolled</th>
                                                <th>Monitor %</th>
                                                <th>Survey %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                completedWithoutRevisitPatients.map((p: (PatientVisit & CompassParticipant)) => {
                                                    const daysBetween = 30;
                                                    return (
                                                        <tr>
                                                            <td><a href={`/compass/${p.study_id}`} target="_blank">{p.study_id}</a></td>
                                                            <td>{p.enrollment_date}</td>
                                                            <td>{p.age}</td>
                                                            <td>{p.gender}</td>
                                                            <td>{daysBetween}</td>
                                                            <td>{Math.round(1000 * Number(p.monitor_counts) / daysBetween) / 10} ({p.monitor_counts}/{daysBetween})</td>
                                                            <td>{Math.round(1000 * Number(p.survey_counts) / daysBetween) / 10} ({p.survey_counts}/{daysBetween})</td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                            <Row style={{marginTop: '64px'}}>
                                <h4>Withdrawn Participants (n={numCompleted})</h4>
                                {
                                    withdrawnPatientsStats && (
                                        <ul>
                                            <li>Median Age: {withdrawnPatientsStats.median_age} (IQR: {withdrawnPatientsStats.iqr_age.Q1}-{withdrawnPatientsStats.iqr_age.Q3})</li>
                                            <li>Female: {withdrawnPatientsStats.female}%</li>
                                        </ul>
                                    )
                                }
                                <Col className='table-fixed-header'>
                                    <Table size="sm">
                                        <thead>
                                            <tr>
                                                <th>Study ID</th>
                                                <th>Enrollment Date</th>
                                                <th>Age</th>
                                                <th>Gender</th>
                                                <th>Monitor Days</th>
                                                <th>Survey Days</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                withdrawnPatients.map((p: (PatientVisit & CompassParticipant)) => {
                                                    return (
                                                        <tr>
                                                            <td><a href={`/compass/${p.study_id}`} target="_blank">{p.study_id}</a></td>
                                                            <td>{p.enrollment_date}</td>
                                                            <td>{p.age}</td>
                                                            <td>{p.gender}</td>
                                                            <td>{p.monitor_counts ?? "-"}</td>
                                                            <td>{p.survey_counts ?? "-"}</td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                            <Row style={{marginTop: '64px'}}>
                                <h4>Past Medical History</h4>
                                <p>
                                    Prevalence of pre-existing conditions among {numEnrolled} enrolled participants:
                                </p>
                                <Col className='table-fixed-header'>
                                    <Table size="sm">
                                        <thead>
                                            <tr>
                                                <th>Category</th>
                                                <th>Count</th>
                                                <th>%</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                Object.keys(pastMedicalHistory).map((text: any) => {
                                                    return (
                                                        <tr>
                                                            <td>{text}</td>
                                                            <td>{pastMedicalHistory[text]}</td>
                                                            <td>{Math.round(1000 * pastMedicalHistory[text] / numEnrolled) / 10}%</td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        </>
                    )
                }
            </Container>
        </>
    );
};
